import { defineComponent as _defineComponent } from 'vue'
import CcCard from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCard.ts.vue';
import CcCardItem from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardItem.ts.vue';
import CcError from '@/views/Automation/CampaignCreatorV2/components/CcError/CcError.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import CcRadioGroupText from '@/views/Automation/CampaignCreatorV2/components/CcRadioGroup/CcRadioGroupText.ts.vue';
import TreeselectAccountOption from '@/views/Automation/CampaignCreatorV2/components/Treeselect/TreeselectAccountOption.ts.vue';
import TreeselectInfoOption from '@/views/Automation/CampaignCreatorV2/components/Treeselect/TreeselectInfoOption.ts.vue';
import * as AdSetMeta from '@/views/Automation/CampaignCreatorV2/helpers/facebook/AdSet';
import {
  BillingEvent,
  CustomEventOptions,
  CustomEventType,
  DestinationType,
  ExclusiveEventsOnly,
  OptimizationGoal,
} from '@/views/Automation/CampaignCreatorV2/helpers/facebook/AdSet';
import * as CampaignMeta from '@/views/Automation/CampaignCreatorV2/helpers/facebook/Campaign';
import useAdAccountStore from '@/views/Automation/CampaignCreatorV2/store/AdAccount';
import useAdSetStore from '@/views/Automation/CampaignCreatorV2/store/AdSet';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import { CardLayer, TreeselectOptionAdAccount, TreeselectOptionInfo } from '@/views/Automation/CampaignCreatorV2/types';
import { AdSetDTO } from '@/views/Automation/CampaignCreatorV2/validation/AdSetDTO';
import Treeselect from '@riophae/vue-treeselect';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import useABVariableStore from '../../store/AbVariables';
import { SupportedEntities } from '../../store/Types';


export default /*#__PURE__*/_defineComponent({
  __name: 'Conversions.ts',
  setup(__props) {

const adAccountStore = useAdAccountStore();

const adsetStore = useAdSetStore();
const { possibleConversionLocations, performanceGoalOptions, getField } = storeToRefs(adsetStore);
const adsetGetters = storeToRefs(adsetStore);

const campaignStore = useCampaignStore();
const campaignGetters = storeToRefs(campaignStore);

const advancedSettingsOpened = ref(false);

const { getPixelsOptions, getCustomEventList } = storeToRefs(adAccountStore);

const isBudgetManagedInCampaignLevel = computed(() => campaignGetters.getIsCampaignBudgetActive.value);

const bidStrategy = computed(() => {
  const bidStrategy = adsetGetters.getField.value('bid_strategy')?.value?.[0] || {};
  bidStrategy.value = bidStrategy.value || AdSetMeta.BidStrategy.COST_CAP;
  return bidStrategy;
});

const isCampaignAuction = computed(
  () => campaignGetters.getField.value('buying_type').value?.[0]?.value === CampaignMeta.BuyingType.AUCTION
);

function onBidStrategyChange(value: { id: any }) {
  adsetStore.addOrEditVariable('bid_strategy', {
    field: 'bid_strategy',
    value: value.id,
    variableId: 'bid_strategy',
  });
}

const getChargedOptions: TreeselectOptionInfo[] = [
  {
    id: BillingEvent.IMPRESSIONS,
    label: 'Impressions',
  },
  {
    id: BillingEvent.LINK_CLICKS,
    label: 'Link Clicks (CPC)',
    info: 'You must first spend at least $10 in this ad account',
    isDisabled: true,
  },
];

const pixelOptions = computed((): TreeselectOptionAdAccount[] =>
  getPixelsOptions.value?.map(
    (p): TreeselectOptionAdAccount => ({
      id: p.id,
      label: p.name,
      info: p.id,
      isActive: true,
      isDisabled: p.id === 'loading',
    })
  )
);
const shouldRenderPixelSelection = computed(() => adsetGetters.isEnabledPixelSelection.value);

const attributionSpec = computed(() => getField.value('attribution_spec')?.value?.[0]);

function updateAttributionSpec(event: AdSetMeta.AttributionSpecEvent, value: { id: number }) {
  adsetStore.addOrEditVariable('attribution_spec', {
    field: 'attribution_spec',
    value: {
      ...(attributionSpec?.value?.value || {}),
      [event]: +value.id,
    },
    variableId: attributionSpec.value.variableId || 'attribution_spec',
  });
}

const isAttributionSpecEnabled = computed(() => {
  const currentObjective = campaignGetters.getField.value('objective')?.value?.[0]?.value;
  const performanceGoal = getField.value('optimization_goal')?.value?.[0]?.value;
  return AdSetDTO.isAttributionSettingsEnabled(currentObjective, performanceGoal);
});

const performanceGoalValue = computed(() => getField.value('optimization_goal')?.value?.[0]);

const customConversionOptions = computed(() => {
  const currentSelectedPixel = getField.value('promoted_object')?.value?.[0]?.value || {};
  const campaignObjective = campaignGetters.getField.value('objective')?.value?.[0]?.value;
  const supportedList = ExclusiveEventsOnly[campaignObjective as keyof typeof ExclusiveEventsOnly] || [];
  const pixelId = currentSelectedPixel.pixel_id;
  const customConversions = getCustomEventList.value?.filter((conversion) => conversion.pixel === pixelId) || [];
  const staticOptions = CustomEventOptions.map((t) => ({
    id: t.id,
    label: t.label,
    custom_event_str: t.label,
    custom_event_type: t.id,
    pixel: '',
    isDisabled: supportedList.length ? !supportedList.includes(t.id) : false,
  }));
  const value = customConversions
    .filter((c) => !CustomEventOptions.some((ce) => ce.label === (c as any).custom_event_str))
    .concat(staticOptions);
  return value;
});

const isBidCapEnable = computed(() => {
  const billingEvent = campaignGetters.getField.value('bid_strategy')?.value?.[0]?.value;
  return ![CampaignMeta.BidStrategy.LOWEST_COST_WITHOUT_CAP].includes(billingEvent);
});

const selectedPixel = computed(() => getField.value('promoted_object')?.value?.[0]);
const selectedCustomConversion = computed(() => getField.value('promoted_object')?.value?.[0]);

const getConversionLocationOptions = computed(() => {
  const options = possibleConversionLocations?.value || [];
  return options;
});

const performanceGoalOptionsValue = computed(() => performanceGoalOptions.value);
const getChargedValue = computed(() => getField.value('billing_event')?.value?.[0]);
const conversionLocationValue = computed(() => getField.value('destination_type')?.value?.[0]);
const bidAmount = computed(() => {
  const goal = getField.value('bid_amount')?.value?.[0];
  const newVal = goal?.value ? goal.value / 100 : goal?.value;
  return {
    ...goal,
    value: newVal,
  };
});

function updatePerformanceGoalValue(value: { id: OptimizationGoal }) {
  adsetStore.addOrEditVariable('optimization_goal', {
    field: 'optimization_goal',
    value: value.id,
    variableId: 'optimization_goal',
  });
  if (!adsetGetters.isEnabledPixelSelection.value) {
    adsetStore.resetField('promoted_object');
  }
}

function updateConversionLocationValue(value: string) {
  adsetStore.addOrEditVariable('destination_type', {
    field: 'destination_type',
    value: value as DestinationType,
    variableId: 'destination_type',
  });
}

function updateGetChargedEvent(value: { id: BillingEvent }) {
  adsetStore.addOrEditVariable('billing_event', {
    field: 'billing_event',
    value: value.id,
    variableId: 'billing_event',
  });
}

function updatePixel(pixel: { id: string }) {
  const currentPromotedObject = getField.value('promoted_object')?.value?.[0]?.value || {};
  const patch = {
    ...currentPromotedObject,
    pixel_id: pixel.id,
  };
  adsetStore.addOrEditVariable('promoted_object', {
    field: 'promoted_object',
    value: patch,
    variableId: 'promoted_object',
  });
}

function updateCustomConversion(value: {
  id: string;
  name: string;
  custom_event_type: CustomEventType;
  custom_event_str?: string;
  pixel: string;
}) {
  const currentPromotedObject = getField.value('promoted_object')?.value?.[0]?.value || {};
  const patch: typeof currentPromotedObject = {
    ...currentPromotedObject,
    custom_event_type: value.custom_event_type,
  };
  const customEventType = CustomEventOptions.find((t) => t.label === value.custom_event_str)?.id;
  if (!customEventType) {
    patch.custom_event_type = CustomEventType.OTHER;
    patch.custom_event_str = value.custom_event_str;
  } else {
    patch.custom_event_type = customEventType;
    patch.custom_event_str = undefined;
    patch.custom_conversion_id = undefined;
  }
  adsetStore.addOrEditVariable('promoted_object', {
    field: 'promoted_object',
    value: patch,
    variableId: 'promoted_object',
  });
}

function updateBidAmount(value: string) {
  if (!parseFloat(value)) {
    useABVariableStore().resetField(SupportedEntities.adset, 'bid_amount');
    useABVariableStore().resetField(SupportedEntities.adset, 'bid_strategy');
    return;
  }
  adsetStore.addOrEditVariable('bid_amount', {
    field: 'bid_amount',
    value: parseFloat(value) * 100,
    variableId: 'bid_amount',
  });
}

function onOpenAdvancedSettings() {
  advancedSettingsOpened.value = !advancedSettingsOpened.value;
}

return { __sfc: true,adAccountStore, adsetStore, possibleConversionLocations, performanceGoalOptions, getField, adsetGetters, campaignStore, campaignGetters, advancedSettingsOpened, getPixelsOptions, getCustomEventList, isBudgetManagedInCampaignLevel, bidStrategy, isCampaignAuction, onBidStrategyChange, getChargedOptions, pixelOptions, shouldRenderPixelSelection, attributionSpec, updateAttributionSpec, isAttributionSpecEnabled, performanceGoalValue, customConversionOptions, isBidCapEnable, selectedPixel, selectedCustomConversion, getConversionLocationOptions, performanceGoalOptionsValue, getChargedValue, conversionLocationValue, bidAmount, updatePerformanceGoalValue, updateConversionLocationValue, updateGetChargedEvent, updatePixel, updateCustomConversion, updateBidAmount, onOpenAdvancedSettings, CcCard, CcCardItem, CcError, CcIcon, CcRadioGroupText, TreeselectAccountOption, TreeselectInfoOption, AdSetMeta, CardLayer, Treeselect }
}

})